/**
 * Runtime API Configuration
 * 
 * Automatically detects the correct backend URL based on the current hostname.
 * This eliminates dependency on build-time environment variables that can get cached.
 */

// Build info for version checking
export const BUILD_INFO = {
  version: process.env.REACT_APP_BUILD_VERSION || 'dev',
  buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString(),
  features: ['supabase-only-auth', 'admin-migration', 'squidboard-fix']
};

// Log build info on load
if (typeof window !== 'undefined') {
  console.log(`[Squidemy] Version: ${BUILD_INFO.version} | Built: ${BUILD_INFO.buildTime}`);
}

const getBackendUrl = () => {
  // If we're in a non-browser environment (SSR), use env variable
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Emergent preview environments - HIGHEST PRIORITY
  if (hostname.includes('.preview.emergentagent.com')) {
    // Use same origin - API is on the same preview domain
    return `${protocol}//${hostname}`;
  }

  // Emergent production deployments (.emergent.host) - HIGH PRIORITY
  if (hostname.includes('.emergent.host')) {
    // Use same origin - don't rely on cached env vars
    return `${protocol}//${hostname}`;
  }

  // Production domains - squidemy.com / squidemy.us
  if (hostname === 'squidemy.com' || hostname === 'www.squidemy.com' ||
      hostname === 'squidemy.us' || hostname === 'www.squidemy.us') {
    // Use same origin for Emergent deployment, or Railway for legacy
    // Check if we have an Emergent env var set
    if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.includes('emergent')) {
      return `${protocol}//${hostname}`;
    }
    // Fallback to Railway for legacy deployment
    return 'https://squidhub-production.up.railway.app';
  }

  // Vercel deployment domains
  if (hostname.includes('vercel.app') || hostname.includes('squid-hub')) {
    // Production backend on Railway
    return 'https://squidhub-production.up.railway.app';
  }

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Use env variable if set, otherwise default to localhost:8001
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }

  // Fallback: Use same origin (safer than potentially stale env var)
  return '';
};

// Cache the URL on first call
let cachedBackendUrl = null;

export const getApiBaseUrl = () => {
  if (cachedBackendUrl === null) {
    cachedBackendUrl = getBackendUrl();
    console.log('[API Config] Backend URL:', cachedBackendUrl || '(same origin)');
  }
  return cachedBackendUrl;
};

// Convenience export for API endpoints
export const API = `${getApiBaseUrl()}/api`;

// For components that need the base URL without /api
export const BACKEND_URL = getApiBaseUrl();

export default API;
